import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useWebsiteContent } from "@/contexts/WebsiteContentContext";
import { usePreview } from "@/contexts/PreviewContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";

const DESCRIPTION_MAX_LINES = 5;

const About = () => {
  const { t } = useTranslation();
  const { content, isLoading } = useWebsiteContent();
  const { previewContent, isPreviewMode } = usePreview();
  const { language } = useLanguage();
  const lang = (language.startsWith('en') ? 'en' : 'ar') as 'en' | 'ar';
  const [isExpanded, setIsExpanded] = useState(false);

  // Use preview content when in preview mode, otherwise use regular content
  const activeContent = (isPreviewMode && previewContent) ? previewContent : content;

  // Don't render if content is still loading
  if (isLoading) {
    return (
      <section id="about" className="py-24 bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
      </section>
    );
  }

  const about = activeContent?.about;

  // Simplified safety check
  if (!about) {
    return (
      <section id="about" className="py-24 bg-white">
        <div className="container px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto text-center">
          <p className="text-muted-foreground">About content not available</p>
        </div>
      </section>
    );
  }

  return (
    <section id="about" className="py-24 bg-white">
      <div className="container px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <div className="text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
            {about.title?.[lang] || 'About ABVI'}
          </h2>
          <h3 className="text-xl text-primary mb-8 font-semibold">
            {about.subtitle?.[lang] || 'Preserving Knowledge, Enabling Access'}
          </h3>
          <p
            className="text-lg text-foreground/70 leading-relaxed transition-all duration-300"
            style={!isExpanded ? {
              display: "-webkit-box",
              WebkitLineClamp: DESCRIPTION_MAX_LINES,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            } : undefined}
          >
            {about.description?.[lang] || 'ABVI Publication Management System is dedicated to digitizing and publishing books, making knowledge accessible to readers worldwide.'}
          </p>
          <Button
            variant="link"
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-4 text-primary hover:text-primary/80"
          >
            {isExpanded ? t("about.seeLess") : t("about.seeMore")}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default About;
